<template>
  <v-dialog v-model="dialog" persistent max-width="750">
    <v-card>
      <v-card-title>
        <v-icon left color="primary">mdi-clipboard-plus</v-icon>
        {{ isEdit ? "Edit Task" : "Assign New Task" }}
      </v-card-title>
      <v-card-text>
        <!-- Template selector -->
        <v-autocomplete
          v-if="!isEdit"
          v-model="selectedTemplate"
          :items="active_templates"
          item-text="name"
          item-value="id"
          label="From Template (optional)"
          clearable
          dense
          outlined
          return-object
          @change="onTemplateSelect"
          class="mb-2"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.name }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ item.category || "" }} - {{ item.default_priority }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>

        <v-text-field
          v-model="form.title"
          label="Task Title *"
          outlined
          dense
          :rules="[rules.required]"
        ></v-text-field>

        <v-textarea
          v-model="form.description"
          label="Description"
          outlined
          dense
          rows="2"
        ></v-textarea>

        <v-row dense>
          <v-col cols="6">
            <v-combobox
              v-model="form.category"
              :items="categoryOptions"
              label="Category"
              outlined
              dense
            ></v-combobox>
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="form.priority"
              :items="priorityOptions"
              label="Priority"
              outlined
              dense
            ></v-select>
          </v-col>
        </v-row>

        <!-- Task Type Toggle -->
        <v-radio-group v-model="taskType" row class="mt-0">
          <v-radio label="One-Time" value="one_time"></v-radio>
          <v-radio label="Recurring" value="recurring"></v-radio>
        </v-radio-group>

        <!-- One-Time Fields -->
        <v-row dense v-if="taskType === 'one_time'">
          <v-col cols="6">
            <v-menu
              v-model="specificDateMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.specific_date"
                  label="Date *"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.specific_date"
                @input="specificDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="form.scheduled_time"
              label="Time"
              type="time"
              outlined
              dense
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Recurring Fields -->
        <div v-if="taskType === 'recurring'">
          <v-row dense>
            <v-col cols="4">
              <v-select
                v-model="form.recurrence_type"
                :items="recurrenceOptions"
                label="Frequency"
                outlined
                dense
              ></v-select>
            </v-col>
            <v-col cols="4">
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.start_date"
                    label="Start Date *"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.start_date"
                  @input="startDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="4">
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.end_date"
                    label="End Date (optional)"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    clearable
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.end_date"
                  @input="endDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>

          <v-text-field
            v-model="form.scheduled_time"
            label="Scheduled Time"
            type="time"
            outlined
            dense
            class="mt-1"
          ></v-text-field>

          <!-- Day-of-week checkboxes (Daily or Weekly) -->
          <div
            v-if="
              form.recurrence_type === 'daily' ||
              form.recurrence_type === 'weekly'
            "
            class="mb-3"
          >
            <div class="subtitle-2 mb-1">
              {{
                form.recurrence_type === "daily"
                  ? "Specific Days (leave empty for every day):"
                  : "Days of Week:"
              }}
            </div>
            <v-chip-group
              v-model="selectedWeekDays"
              multiple
              active-class="primary--text"
            >
              <v-chip v-for="day in weekDays" :key="day.value" filter outlined>
                {{ day.text }}
              </v-chip>
            </v-chip-group>
          </div>

          <!-- Monthly: day-of-month selector -->
          <v-select
            v-if="form.recurrence_type === 'monthly'"
            v-model="selectedMonthDays"
            :items="monthDays"
            label="Days of Month"
            multiple
            outlined
            dense
            chips
            small-chips
          ></v-select>
        </div>

        <!-- Assign To -->
        <v-autocomplete
          v-model="form.assigned_to"
          :items="users"
          item-text="full_name"
          item-value="id"
          :label="isEdit ? 'Assigned To *' : 'Assign To * (multiple allowed)'"
          :multiple="!isEdit"
          outlined
          dense
          chips
          small-chips
          deletable-chips
          :rules="[rules.required]"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.full_name }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ item.branch ? item.branch.branch_name : "" }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="loading" @click="submit">
          <v-icon left>{{ isEdit ? "mdi-pencil" : "mdi-plus" }}</v-icon>
          {{ isEdit ? "Update" : "Assign" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
export default {
  props: {
    value: { type: Boolean, default: false },
    editTask: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      selectedTemplate: null,
      taskType: "one_time",
      specificDateMenu: false,
      startDateMenu: false,
      endDateMenu: false,
      selectedWeekDays: [],
      selectedMonthDays: [],
      form: this.getEmptyForm(),
      rules: {
        required: (v) =>
          (v !== null &&
            v !== undefined &&
            v !== "" &&
            (!Array.isArray(v) || v.length > 0)) ||
          "Required",
      },
      priorityOptions: [
        { text: "Basic", value: "basic" },
        { text: "Urgent", value: "urgent" },
        { text: "Very Urgent", value: "very_urgent" },
      ],
      recurrenceOptions: [
        { text: "Daily", value: "daily" },
        { text: "Weekly", value: "weekly" },
        { text: "Monthly", value: "monthly" },
      ],
      categoryOptions: [
        "Accounting",
        "Inventory",
        "Maintenance",
        "Administration",
        "Sales",
        "Service",
      ],
      weekDays: [
        { text: "Mon", value: 1 },
        { text: "Tue", value: 2 },
        { text: "Wed", value: 3 },
        { text: "Thu", value: 4 },
        { text: "Fri", value: 5 },
        { text: "Sat", value: 6 },
        { text: "Sun", value: 7 },
      ],
      monthDays: Array.from({ length: 31 }, (_, i) => i + 1),
    };
  },
  computed: {
    ...mapGetters({
      users: "auth/users",
      active_templates: "task_template/active_templates",
    }),
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isEdit() {
      return !!this.editTask;
    },
  },
  watch: {
    value(val) {
      if (val) {
        if (this.editTask) {
          this.loadEditData();
        } else {
          this.form = this.getEmptyForm();
          this.taskType = "one_time";
          this.selectedTemplate = null;
          this.selectedWeekDays = [];
          this.selectedMonthDays = [];
        }
        if (!this.users || this.users.length === 0) {
          this.get_users();
        }
        if (!this.active_templates || this.active_templates.length === 0) {
          this.get_templates();
        }
      }
    },
  },
  methods: {
    ...mapActions({
      create_task: "reminder/create_task",
      update_task: "reminder/update_task",
      get_users: "auth/get_users",
      get_templates: "task_template/get_templates",
      set_notification: "notification/set_notification",
    }),
    getEmptyForm() {
      return {
        title: "",
        description: "",
        category: "",
        priority: "basic",
        recurrence_type: "none",
        recurrence_days: null,
        scheduled_time: null,
        start_date: new Date().toISOString().substr(0, 10),
        end_date: null,
        specific_date: new Date().toISOString().substr(0, 10),
        assigned_to: [],
        task_template_id: null,
      };
    },
    parseDays(raw) {
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw);
        } catch (_) {
          return [];
        }
      }
      return [];
    },
    loadEditData() {
      const t = this.editTask;
      const days = this.parseDays(t.recurrence_days);
      this.form = {
        id: t.id,
        title: t.title,
        description: t.description,
        category: t.category,
        priority: t.priority,
        recurrence_type: t.recurrence_type,
        recurrence_days: days,
        scheduled_time: t.scheduled_time,
        start_date: t.start_date ? t.start_date.substr(0, 10) : null,
        end_date: t.end_date ? t.end_date.substr(0, 10) : null,
        specific_date: t.specific_date ? t.specific_date.substr(0, 10) : null,
        assigned_to:
          t.assigned_to && typeof t.assigned_to === "object"
            ? t.assigned_to.id
            : t.assigned_to,
        task_template_id: t.task_template_id,
        status: t.status,
      };
      this.taskType = t.recurrence_type === "none" ? "one_time" : "recurring";
      if (
        (t.recurrence_type === "daily" || t.recurrence_type === "weekly") &&
        days.length > 0
      ) {
        this.selectedWeekDays = days
          .map((d) => this.weekDays.findIndex((wd) => wd.value === Number(d)))
          .filter((i) => i !== -1);
      }
      if (t.recurrence_type === "monthly" && days.length > 0) {
        this.selectedMonthDays = days.map(Number);
      }
    },
    onTemplateSelect(template) {
      if (!template) return;
      this.form.title = template.name;
      this.form.description = template.description || "";
      this.form.category = template.category || "";
      this.form.priority = template.default_priority || "basic";
      this.form.scheduled_time = template.default_time;
      this.form.task_template_id = template.id;
      if (template.default_recurrence_type !== "none") {
        this.taskType = "recurring";
        this.form.recurrence_type = template.default_recurrence_type;
        if (
          (template.default_recurrence_type === "daily" ||
            template.default_recurrence_type === "weekly") &&
          template.default_recurrence_days
        ) {
          this.selectedWeekDays = template.default_recurrence_days
            .map((d) => this.weekDays.findIndex((wd) => wd.value === Number(d)))
            .filter((i) => i !== -1);
        }
        if (
          template.default_recurrence_type === "monthly" &&
          template.default_recurrence_days
        ) {
          this.selectedMonthDays = template.default_recurrence_days;
        }
      } else {
        this.taskType = "one_time";
      }
    },
    close() {
      this.dialog = false;
    },
    submit() {
      if (!this.form.title) {
        this.$alert({
          title: "Validation",
          message: "Task title is required",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (
        !this.isEdit &&
        (!this.form.assigned_to || this.form.assigned_to.length === 0)
      ) {
        this.$alert({
          title: "Validation",
          message: "Please select at least one user to assign",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (this.isEdit && !this.form.assigned_to) {
        this.$alert({
          title: "Validation",
          message: "Please select a user to assign",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (this.taskType === "one_time" && !this.form.specific_date) {
        this.$alert({
          title: "Validation",
          message: "Please select a date for this task",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (this.taskType === "recurring" && !this.form.start_date) {
        this.$alert({
          title: "Validation",
          message: "Please select a start date for the recurring task",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }

      // Build payload
      const payload = { ...this.form };

      if (this.taskType === "one_time") {
        payload.recurrence_type = "none";
        payload.recurrence_days = null;
        payload.start_date = payload.specific_date;
      } else {
        payload.specific_date = null;
        // Build recurrence_days from selections
        if (
          payload.recurrence_type === "daily" ||
          payload.recurrence_type === "weekly"
        ) {
          const days = this.selectedWeekDays.map(
            (idx) => this.weekDays[idx].value
          );
          if (payload.recurrence_type === "weekly" && days.length === 0) {
            this.$alert({
              title: "Validation",
              message: "Please select at least one day for weekly recurrence",
              color: "warning",
              icon: "mdi-alert",
            });
            return;
          }
          payload.recurrence_days = days.length > 0 ? days : null;
        } else if (payload.recurrence_type === "monthly") {
          if (!this.selectedMonthDays || this.selectedMonthDays.length === 0) {
            this.$alert({
              title: "Validation",
              message: "Please select at least one day for monthly recurrence",
              color: "warning",
              icon: "mdi-alert",
            });
            return;
          }
          payload.recurrence_days = this.selectedMonthDays;
        } else {
          payload.recurrence_days = null;
        }
      }

      this.loading = true;
      const action = this.isEdit ? this.update_task : this.create_task;
      action(payload)
        .then(() => {
          this.loading = false;
          this.set_notification({
            message: this.isEdit
              ? "Task updated successfully!"
              : "Task assigned successfully!",
            type: "success",
          });
          this.$emit("saved");
          this.close();
        })
        .catch((err) => {
          this.loading = false;
          this.$alert({
            title: "Error",
            message: err.response ? err.response.data.error : err.message,
            color: "error",
            icon: "mdi-alert-circle",
          });
        });
    },
  },
};
</script>
